import React from 'react';
import { Link } from 'react-router-dom';

interface InquiryModalProps {
    show: boolean;
    onClose: () => void;
    project: any;
    offer: any;
    handleInquirySubmit: (e: React.FormEvent) => void;
    inquiryForm: { 
        name: string; 
        email: string; 
        phone: string; 
        agreeContact: boolean; 
        agreeTerms: boolean;
        interestedHomeLoan: boolean;
        purpose: string;
        propertyType: string;
        propertySubType: string;
    };
    setInquiryForm: React.Dispatch<React.SetStateAction<{ 
        name: string; 
        email: string; 
        phone: string; 
        agreeContact: boolean; 
        agreeTerms: boolean;
        interestedHomeLoan: boolean;
        purpose: string;
        propertyType: string;
        propertySubType: string;
    }>>;
    inquirySubmitting: boolean;
    inquirySuccess: string;
    inquiryError: string;
}

const propertySubTypes: Record<string, string[]> = {
    'Residential': ['Apartment', 'Independent House/Villa', 'Floor', 'Bungalow', 'Penthouse', 'Duplex'],
    'Commercial': ['Office Space', 'Shop/Showroom', 'Commercial Land', 'Warehouse/Godown', 'Industrial Shed', 'Hotel/Guest House'],
    'PG/Co-living': ['Girls PG', 'Boys PG', 'Co-living Space', 'Guest House'],
    'Plot': ['Residential Plot', 'Commercial Plot', 'Industrial Plot', 'Agricultural Land'],
    'Land': ['Agricultural Land', 'Industrial Land', 'Institutional Land'],
    'Bank Auction Property': ['Residential', 'Commercial', 'Industrial', 'Agricultural']
};

const InquiryModal: React.FC<InquiryModalProps> = ({
    show,
    onClose,
    project,
    offer,
    handleInquirySubmit,
    inquiryForm,
    setInquiryForm,
    inquirySubmitting,
    inquirySuccess,
    inquiryError
}) => {
    if (!show) return null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-[#05144b]/60 backdrop-blur-md"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div className="relative w-full max-w-[500px] bg-white rounded-[2.5rem] shadow-[0_30px_100px_rgba(0,0,0,0.25)] overflow-hidden border border-white/20">
                {/* Design Accent: Gold Vertical Ribbon */}
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#f1ae24]"></div>

                {/* Authority Mascot: Full Width Banner */}
                <div className="w-full overflow-hidden">
                    <img 
                        src="/assets/img/monkey.png" 
                        alt="My Property Guru Mascot" 
                        className="w-full h-auto object-contain animate-color-tone"
                    />
                </div>

                <div className="p-6 md:p-8 pt-0 md:pt-0">
                    <style>{`
                        @keyframes color-tone {
                            0% { filter: brightness(1) saturate(1) hue-rotate(0deg); }
                            50% { filter: brightness(1.1) saturate(1.2) hue-rotate(5deg); }
                            100% { filter: brightness(1) saturate(1) hue-rotate(0deg); }
                        }
                        .animate-color-tone {
                            animation: color-tone 8s ease-in-out infinite;
                        }
                        @keyframes bounce-subtle {
                            0%, 100% { transform: translateY(0); }
                            50% { transform: translateY(-5px); }
                        }
                        .animate-bounce-subtle {
                            animation: bounce-subtle 3s ease-in-out infinite;
                        }
                    `}</style>

                    {/* Close Button */}
                    <button 
                        onClick={onClose}
                        className="absolute top-6 right-6 w-8 h-8 rounded-full bg-[#05144b]/5 flex items-center justify-center hover:bg-[#05144b]/10 transition-colors"
                    >
                        <i className="fa-solid fa-xmark text-[#05144b] text-sm"></i>
                    </button>

                    {/* Header */}
                    <div className="text-center mb-2">
                        <div className="inline-flex items-center gap-2 bg-[#05144b]/5 border border-[#f1ae24]/20 px-3 py-1.5 rounded-full mb-2 shadow-sm">
                            <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"></span>
                            <span className="text-[#05144b] text-[10px] font-black italic">Priority Allocation Protocol</span>
                        </div>
                        <h4 className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95] mb-0 italic leading-none">
                            {offer ? 'Claim Reward' : 'Request Access'}
                        </h4>
                        {offer && (
                            <div className="mt-1 bg-amber-400/10 border border-amber-400/20 px-3 py-1 rounded-xl inline-block">
                                <span className="text-amber-800 text-[10px] font-black">{offer.example} Verified</span>
                            </div>
                        )}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleInquirySubmit} className="space-y-3">


                        <div className="space-y-4">
                            <div className="relative group/field">
                                <label className="text-[11px] font-black text-[#05144b]/70 mb-1.5 block ml-1">Your Name</label>
                                <div className="relative">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[12px]">
                                        👤
                                    </div>
                                    <input
                                        type="text"
                                        className="w-full bg-[#05144b]/5 border-0 border-b-2 border-transparent pl-11 pr-4 py-3.5 rounded-xl text-xs font-black text-[#05144b] placeholder-[#05144b]/40 focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                                        placeholder="Full Name"
                                        required
                                        value={inquiryForm.name}
                                        onChange={e => setInquiryForm(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>
                            </div>

                            <div className="relative group/field">
                                <label className="text-[11px] font-black text-[#05144b]/70 mb-1.5 block ml-1">Your Mobile Number</label>
                                <div className="relative">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[12px]">
                                        📱
                                    </div>
                                    <input
                                        type="tel"
                                        className="w-full bg-[#05144b]/5 border-0 border-b-2 border-transparent pl-11 pr-4 py-3.5 rounded-xl text-xs font-black text-[#05144b] placeholder-[#05144b]/40 focus:bg-white focus:shadow-xl focus:border-[#f1ae24] transition-all outline-none"
                                        placeholder="Number"
                                        required
                                        value={inquiryForm.phone}
                                        onChange={e => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                                    />
                                </div>
                            </div>
                        </div>



                        {/* Terms and Conditions Opt-in */}
                        <div 
                            className="flex bg-blue-500/5 p-3 rounded-2xl border border-blue-500/10 gap-3 group/optin cursor-pointer" 
                        >
                            <div 
                                className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all ${inquiryForm.agreeTerms ? 'bg-blue-500 border-blue-500' : 'border-blue-500/30 group-hover/optin:border-emerald-500'}`}
                                onClick={() => setInquiryForm(prev => ({ ...prev, agreeTerms: !prev.agreeTerms }))}
                            >
                                {inquiryForm.agreeTerms && <span className="text-[10px]">✅</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-black text-[#05144b] leading-tight italic">
                                    <Link to="/terms" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>Terms & Conditions</Link>
                                </p>
                                <p className="text-[9px] font-bold text-[#05144b]/50 leading-tight">
                                    I agree to the <Link to="/privacy-policy" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>privacy policy</Link> and <Link to="/terms" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>terms of use</Link>
                                </p>
                            </div>
                        </div>

                        {inquiryError && (
                            <div className="bg-rose-50 border border-rose-100 p-3 rounded-2xl flex items-center gap-3">
                                <i className="fa-solid fa-triangle-exclamation text-rose-500"></i>
                                <span className="text-rose-600 text-[10px] font-black uppercase tracking-tight">{inquiryError}</span>
                            </div>
                        )}
                        {inquirySuccess && (
                            <div className="bg-emerald-50 border border-emerald-100 p-3 rounded-2xl flex items-center gap-3">
                                <i className="fa-solid fa-circle-check text-emerald-500"></i>
                                <span className="text-emerald-600 text-[10px] font-black uppercase tracking-tight">{inquirySuccess}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={inquirySubmitting}
                            className="w-full relative py-4 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white rounded-2xl border-0 overflow-hidden group shadow-2xl active:scale-[0.98] transition-all hover:-translate-y-1 animate-pulse-button"
                        >
                            {/* Continuous Shimmer Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer-continuous pointer-events-none"></div>
                            
                            <div className="relative z-10 flex items-center justify-center gap-3">
                                <span className="text-[14px] font-black italic text-[#f1ae24] uppercase tracking-wider">
                                    {inquirySubmitting ? 'Submitting...' : 'Submit'}
                                </span>
                                {!inquirySubmitting && <i className="fa-solid fa-paper-plane text-[#f1ae24] text-xs animate-plane-fly"></i>}
                            </div>
                        </button>

                        <style>{`
                            @keyframes plane-fly {
                                0% { transform: translate(0, 0) rotate(0); opacity: 1; }
                                20% { transform: translate(2px, -2px) rotate(-5deg); }
                                50% { transform: translate(15px, -15px) rotate(-10deg); opacity: 0; }
                                51% { transform: translate(-15px, 15px) rotate(-10deg); opacity: 0; }
                                100% { transform: translate(0, 0) rotate(0); opacity: 1; }
                            }
                            .animate-plane-fly {
                                animation: plane-fly 3s infinite ease-in-out;
                            }
                            @keyframes shimmer-continuous {
                                0% { transform: translateX(-150%); }
                                100% { transform: translateX(150%); }
                            }
                            .animate-shimmer-continuous {
                                animation: shimmer-continuous 3s infinite linear;
                            }
                            @keyframes pulse-button {
                                0%, 100% { transform: scale(1); box-shadow: 0 10px 15px -3px rgba(5, 20, 75, 0.3); }
                                50% { transform: scale(1.01); box-shadow: 0 20px 25px -5px rgba(5, 20, 75, 0.4); }
                            }
                            .animate-pulse-button {
                                animation: pulse-button 3s ease-in-out infinite;
                            }
                        `}</style>


                        <p className="text-center text-[10px] font-black text-[#05144b]/40 italic">Branded Security Protocol • Privacy Guaranteed</p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default InquiryModal;
